import designEn from '@/design/i18n/en'

import type { MessageSchema } from './sl'

/* English — checked against the Slovenian schema (`sl.ts`): a missing or extra key fails the type-check. */
const en = {
    general: {
        add:      'Add',
        edit:     'Edit',
        delete:   'Delete',
        cancel:   'Cancel',
        save:     'Save',
        close:    'Close',
        confirm:  'Confirm',
        search:   'Search …',
        loading:  'Loading …',
        saving:   'Saving',
        sign_out: 'Sign out',
        open:     'Open',
        actions:  'Options',
        none:     '—',
    },
    brand: {
        product_name: 'Imprion DS',
        tagline:      'Screens · Content · Schedules',
        provided_by:  'Provider',
    },
    nav: {
        overview:       'Overview',
        screens:        'Screens',
        media:          'Media',
        templates:      'Presentations',
        schedules:      'Schedules',
        brand:          'Brand',
        menu:           'Menu',
        tenant_users:   'Users',
        group_provider: 'Provider',
        tenants:        'Customers',
        users:          'Users',
        settings:       'Settings',
    },
    layout: {
        open_menu:  'Open menu',
        close_menu: 'Close menu',
    },
    color_mode: {
        switch_to_light: 'Switch to light mode',
        switch_to_dark:  'Switch to dark mode',
    },
    context: {
        label:       'Customer space',
        help:        'Settings apply to the selected space.',
        placeholder: 'Select a customer',
        inactive:    '{name} (switched off)',
        not_found:   'This customer space cannot be opened.',
    },
    role: {
        admin:          'Administrator',
        editor:         'Editor',
        provider_admin: 'Provider administrator',
    },
    locale: {
        label: 'Language',
        sl:    'Slovenščina',
        en:    'English',
    },
    errors: {
        generic_error:       'Something went wrong.',
        invalid_data:        'The data is not valid.',
        not_found:           'It does not exist (any more).',
        unauthorized:        'Your session has expired. Please sign in again.',
        forbidden:           'You are not allowed to do this.',
        on_add:              'Adding failed.',
        on_edit:             'Saving failed.',
        on_delete:           'Deleting failed.',
        on_load:             'Loading failed.',
        tenant_inactive:     'This customer space is switched off — signing in is not possible.',
        weather_unavailable: 'The weather forecast is not available right now.',
        in_use:              'It is still in use, so it cannot be deleted.',
        in_use_list:         'It is still in use, so it cannot be deleted: {list}',
        pos_not_configured:  'The POS is not connected — a tenant admin has to set the environment, the token and the business unit.',
        pos_failed:          'Imprion POS did not answer or refused the request.',
        pos_failed_reason:   'Imprion POS refused the request: {reason}',
        share_code_invalid:  'The code does not exist, or screen sharing is not switched on for that screen.',
        share_session_ended: 'This sharing session has already ended.',
        domain_taken:        'This sharing address is already used by another look or a customer\'s web address.',
    },
    success: {
        add:    'Added.',
        edit:   'Saved.',
        delete: 'Deleted.',
    },
    validation: {
        required: 'Required.',
        email:    'Invalid e-mail address.',
        too_long: 'Too long.',
        invalid:  'Invalid value.',
    },
    date: {
        pick:  'Pick a date',
        clear: 'Clear the date',
    },
    time: {
        today_at:    'today {time}',
        tomorrow_at: 'tomorrow {time}',
    },
    auth: {
        username:          'Username',
        password:          'Password',
        password_show:     'Show password',
        password_hide:     'Hide password',
        sign_in:           'Sign in',
        or:                'or',
        failed:            'Wrong username or password.',
        too_many_attempts: 'Too many failed attempts. Try again in a few minutes.',
        welcome:           'Sign in to your space.',
    },
    passkey: {
        passkeys:        'Passkeys',
        subtitle:        'Sign in with a fingerprint, your face or a security key instead of a password.',
        sign_in:         'Sign in with a passkey',
        sign_in_failed:  'Signing in with a passkey failed.',
        add:             'Add a passkey',
        added:           'The passkey has been added.',
        failed:          'The passkey could not be added.',
        name:            'Name',
        name_help:       'The name tells the keys apart in the list, e.g. "Work laptop".',
        default_name:    'My device',
        continue:        'Continue',
        created:         'Added',
        last_used:       'Last used',
        never_used:      'Not used yet',
        empty:           'You have no passkeys yet.',
        delete:          'Delete passkey',
        confirm_delete:  'Delete the passkey "{name}"? You will no longer be able to sign in with this device without a password.',
        removed:         'The passkey has been deleted.',
        delete_failed:   'The passkey could not be deleted.',
        unavailable:     'Passkeys are not configured on this server.',
        unsupported:     'This browser does not support passkeys (a secure HTTPS connection is needed).',
    },
    password: {
        change:        'Change password',
        current:       'Current password',
        new:           'New password',
        repeat:        'Repeat the new password',
        mismatch:      'The passwords do not match.',
        changed:       'The password has been changed.',
        failed:        'The password could not be changed.',
        wrong_current: 'The current password is wrong.',
    },
    settings: {
        title:             'Settings',
        subtitle:          'Your account, language and sign-in.',
        profile:           'Profile',
        profile_subtitle:  'The name and e-mail address of your account.',
        account:           'Account',
        tenant:            'Customer space',
        tenants:           'Customer spaces',
        role:              'Role',
        locale_help:       'The language of the application.',
        provider:          'Provider',
        provider_subtitle: 'The provider name screens show when a customer has the provider brand switched on.',
        provider_name:     'Provider name',
        version:           'Version',
    },
    user: {
        users:               'Users',
        subtitle_tenant:     'Who can sign in to the customer space and what they may edit.',
        subtitle_provider:   'The users of every customer and the provider administrators.',
        add:                 'New user',
        edit:                'Edit user',
        delete:              'Delete user',
        name:                'Full name',
        username:            'Username',
        username_rule:       '3–32 characters: lowercase letters, digits, dot, underscore or dash.',
        username_fixed:      'The username cannot be changed.',
        username_taken:      'This username already exists.',
        email:               'E-mail',
        role:                'Role',
        role_help:           'An editor edits media, presentations and schedules; an administrator also screens, the brand and users.',
        space:               'Space',
        tenants:             'Spaces',
        other_tenants:       'Other spaces',
        no_memberships:      'No space',
        filter_all:          'All spaces',
        filter_provider:     'Provider administrators',
        password:            'Password',
        password_reset:      'New password (reset)',
        password_keep:       'Leave empty to keep the password.',
        password_rule:       'At least 8 characters.',
        password_too_short:  'The password must have at least 8 characters.',
        active:              'Active',
        active_help:         'An inactive user cannot sign in.',
        status:              'Status',
        status_active:       'Active',
        status_inactive:     'Inactive',
        last_login:          'Last sign-in',
        never_logged_in:     'Never',
        search:              'Search by name or e-mail …',
        show_inactive:       'Show inactive',
        empty:               'No users.',
        you:                 'You',
        cannot_delete_self:  'You cannot delete your own account.',
        confirm_delete:      'Delete the user {name}? The account is deleted in every space and can no longer sign in.',
        deleted:             'The user has been deleted.',
        delete_failed:       'The user could not be deleted.',
        last_admin:          'This is the last active administrator — at least one must remain.',
        locale:              'Language',
        locale_help:         'The application language for this user.',
        role_in_tenant:      'Role in this space',
        not_manageable:      'This user is in other spaces too — the provider edits their personal data.',
        remove:              'Remove from space',
        confirm_remove:      'Remove the user {name} from this space? If they are a member of other spaces, their account stays there; otherwise they can no longer sign in.',
        removed:             'The user has been removed from the space.',
        remove_failed:       'The user could not be removed from the space.',
        cannot_remove_self:  'You cannot remove yourself from the space.',
        add_existing:        'Add existing',
        add_existing_title:  'Add an existing user',
        add_existing_help:   'The exact username of a person who already has an account in another space.',
        add_existing_action: 'Add to space',
        exists_offer:        'A user with this username already exists. Add the existing user to this space?',
        member_added:        'The user has been added to the space.',
        member_not_found:    'There is no active user with this username.',
        already_member:      'This user is already a member of this space.',
        provider_admin:      'Provider administrator',
        provider_admin_help: 'Manages every customer, user and the provider settings; has administrator rights in every space.',
        memberships:         'Spaces',
        memberships_help:    'Which customer spaces the user works in, and with which role.',
        membership_add:      'Add space',
        membership_remove:   'Remove space',
        memberships_none:    'The user is not in any space yet.',
        memberships_required: 'Add at least one space or switch on provider administrator.',
        memberships_duplicate: 'Each space can be listed only once.',
    },
    tenant: {
        tenants:         'Customers',
        subtitle:        'Each customer has its own space: screens, media, schedules, brand and users.',
        add:             'New customer',
        edit:            'Edit customer',
        delete:          'Delete customer',
        name:            'Name',
        name_placeholder: 'Hotel Lipa',
        slug:            'Slug',
        slug_help:       'Lowercase letters, digits and dashes (2–40 characters). Made from the name.',
        slug_rule:       'Only lowercase letters, digits and dashes, 2–40 characters.',
        slug_taken:      'Another customer already uses this slug.',
        timezone:        'Time zone',
        timezone_help:   'Schedules on the screens run on this time.',
        locale:          'Language',
        location:        'Location',
        location_help:   'The default location for the weather on the customer\'s screens.',
        active:          'Active',
        active_help:     'A switched-off customer cannot sign in and its screens get no content.',
        status:          'Status',
        status_active:   'Active',
        status_inactive: 'Switched off',
        screens:         'Screens',
        users:           'Users',
        created:         'Created',
        open:            'Open space',
        confirm_delete:  'Delete the customer {name}? Its users will no longer be able to sign in and its screens go dark.',
        deleted:         'The customer has been deleted.',
        delete_failed:   'The customer could not be deleted.',
        empty:           'No customers yet.',
        empty_help:      'Add the first customer, then open its space.',
        search:          'Search by name or slug …',
        activated:       'The customer {name} is switched on.',
        deactivated:     'The customer {name} is switched off.',
    },
    location: {
        label:             'Place',
        label_placeholder: 'Ljubljana',
        lat:               'Latitude',
        lon:               'Longitude',
        lat_range:         'Between −90 and 90.',
        lon_range:         'Between −180 and 180.',
    },
    orientation: {
        landscape: 'Landscape',
        portrait:  'Portrait',
        ratio:     '{orientation} ({ratio})',
    },
    status: {
        online:   'Online',
        offline:  'Offline',
        unpaired: 'Not paired',
    },
    overview: {
        title:             'Overview',
        subtitle:          'The state of the screens and publications in the customer space.',
        online:            'Online',
        offline:           'Offline',
        unpaired:          'Not paired',
        unpublished:       'Unpublished changes',
        unpublished_of:    'of {total} designs',
        active_rules:      'Active rules',
        media:             'Media storage',
        media_count:       '{n} file | {n} files',
        screens:           'Screens',
        all_screens:       'All screens',
        no_screens:        'No screens yet.',
        now_playing:       'Playing',
        nothing_playing:   'Nothing',
        last_seen:         'Last seen',
        never_seen:        'Never',
        upcoming:          'Upcoming',
        upcoming_subtitle: 'Rule starts in the next 24 hours.',
        no_upcoming:       'No rule starts in the next 24 hours.',
        all_rules:         'All schedules',
    },
    diagnostics: {
        title:       'Diagnostics',
        version:     'Player version',
        resolution:  'Resolution',
        user_agent:  'Device (user agent)',
        log:         'Device log',
        log_help:    'What the display itself reported: errors, failed downloads, page turns. Kept for 7 days.',
        refresh:     'Refresh',
        details:     'Details',
        empty:       'The device has not reported anything yet.',
        empty_help:  'Entries appear once the display opens the player version 1.1.0 or newer.',
        level_error: 'Error',
        level_warn:  'Warning',
        level_info:  'Info',
    },
    screen: {
        screens:                'Screens',
        subtitle:               'The displays in the customer space, their groups and base content.',
        add:                    'Add screen',
        edit:                   'Edit screen',
        delete:                 'Delete screen',
        name:                   'Name',
        name_placeholder:       'Reception',
        group:                  'Group',
        no_group:               'No group',
        orientation:            'Orientation',
        location:               'Location for the weather',
        location_tenant:        'The customer\'s location',
        location_custom:        'The screen\'s own location',
        default_design:         'Base content',
        default_design_help:    'What the screen shows when no rule applies: a design or a playlist.',
        default_none:           'Brand screen (logo)',
        pairing_code:           'Pairing code',
        pairing_code_help:      'Optional: the code the screen shows. You can also pair it later.',
        pair:                   'Pair',
        pair_title:             'Pair the screen {name}',
        pair_help:              'Enter the code the screen shows.',
        pair_code_rule:         'The code has 6 characters, e.g. K7M-4QX.',
        paired:                 'The screen is paired.',
        pair_invalid:           'The code does not exist, has expired or was already used.',
        repair:                 'Pair again',
        repair_help:            'The old display will be disconnected.',
        unpair:                 'Unpair',
        confirm_unpair:         'Unpair the screen {name}? The display will show a pairing code again.',
        unpaired_done:          'The screen is unpaired.',
        confirm_delete:         'Delete the screen {name}? Rules will no longer target it.',
        deleted:                'The screen has been deleted.',
        connect_title:          'How do I connect a screen?',
        connect_hint:           'On the screen, open {url} and enter the code it shows.',
        now_playing:            'Playing',
        last_seen:              'Last online',
        never_seen:             'Never',
        empty:                  'No screens yet.',
        empty_help:             'Add a screen, then pair it with the code the display shows.',
        empty_filtered:         'There are no screens in this group.',
        filter_all:             'All groups',
        groups:                 'Groups',
        groups_subtitle:        'A group joins screens that rules target together, e.g. "Reception + lobby".',
        group_add:              'Add group',
        group_name:             'Group name',
        group_name_placeholder: 'Reception + lobby',
        group_count:            '{n} screen | {n} screens',
        group_confirm_delete:   'Delete the group {name}? Its screens are left without a group.',
        group_deleted:          'The group has been deleted.',
        groups_empty:           'No groups yet.',
        read_only:              'Screens are managed by the customer\'s administrator.',
        count:                  '{n} screen | {n} screens',
        share:                  'Screen sharing',
        share_help:             'The screen shows a share code, and a laptop shares its screen to it at {url}. Works with the Imprion DS app on Apple TV — the web player does not take shares.',
        share_on:               'Screen sharing is on',
    },
    share: {
        title:                 'Share your screen',
        subtitle:              'Show what is on your computer on the screen in the room.',
        code:                  'Code from the screen',
        code_help:             'The code is in the bottom right corner of the screen, e.g. K7M-4QX.',
        code_rule:             'The code has 6 characters, e.g. K7M-4QX.',
        name:                  'Your name',
        name_help:             'Optional — the screen shows it for a moment.',
        name_placeholder:      'Anna Smith',
        audio:                 'Share sound too',
        audio_help:            'Works in Chrome and Edge on Windows when you share the entire screen.',
        next:                  'Next',
        target:                'Screen',
        pick:                  'Choose what to share',
        pick_help:             'Your browser will ask whether to share the entire screen, a window or a tab.',
        other_network_title: 'This computer is not on the screen’s network',
        other_network_help:  'The share will most likely connect without a picture. Join the network the screen is on (for example the meeting room’s Wi-Fi) and enter the code again.',
        back:                  'Back',
        connecting:            'Connecting to the screen …',
        connecting_help:       'The screen takes the picture in a few seconds.',
        cancel:                'Cancel',
        live_title:            'Sharing to the screen “{screen}”',
        live_elapsed:          'Sharing for {time}',
        live_audio:            'with sound',
        live_hint:             'You can minimise this window, sharing goes on. Stop it here or with your browser’s button.',
        preview:               'Preview of what you share',
        stop:                  'Stop sharing',
        replace:               'Share something else',
        ended_title:           'Sharing has ended',
        ended_problem_title:   'Sharing was interrupted',
        again:                 'Share again',
        end_sender:            'The screen shows its own content again.',
        end_screen:            'The screen closed the connection.',
        end_replaced:          'Somebody else took the screen over.',
        end_expired:           'The screen did not respond.',
        end_disabled:          'Screen sharing is switched off on this screen.',
        end_no_connection:     'Could not connect to the screen. The computer and the screen must be on the same network.',
        end_connection_lost:   'The connection to the screen was lost.',
        end_server_lost:       'The server cannot be reached, so sharing has stopped.',
        end_gone:              'This sharing session no longer exists.',
        end_failed:            'Sharing could not be started.',
        error_code:            'The code does not exist, or screen sharing is not switched on for that screen.',
        error_throttled:       'Too many wrong codes. Try again in a few minutes.',
        error_tenant_inactive: 'This space is switched off, so sharing is not possible.',
        error_network:         'The server cannot be reached. Check your network connection and try again.',
        error_open:            'Sharing could not be started.',
        capture_os_denied:     'macOS does not allow the browser to record the screen. Allow it in System Settings → Privacy & Security → Screen Recording, then restart the browser.',
        capture_no_audio:      'This browser cannot share sound. Untick “Share sound too” and try again.',
        capture_failed:        'Capturing the screen failed. Try again.',
        unsupported_title:     'Share from a computer’s browser',
        unsupported:           'Open this page in Chrome, Edge, Firefox or Safari on a laptop or a desktop computer.',
        unsupported_airplay:   'From an iPhone, iPad or Mac you can also share straight to the Apple TV with AirPlay.',
        insecure_title:        'The page is not open securely',
        insecure:              'A browser shares the screen only from a page opened over https. Open {url}.',
    },
    pending: {
        title:               'Waiting to be confirmed ({n})',
        badge:               'Waiting to be confirmed: {n}',
        badge_open:          'Open screens',
        code_on_screen:      'code on the screen',
        ip:                  'The IP address the screen called from',
        unknown_device:      'Unknown device',
        no_tenant:           'No space',
        same_network:        'Same network',
        same_network_help:   'The screen opened /tv on the same network an admin of this space works from.',
        confirm:             'Confirm',
        dismiss:             'Dismiss',
        dismissed:           'Dismissed — the screen will show a new code.',
        confirm_title:       'Confirm the screen',
        confirm_failed:      'Confirming failed.',
        confirmed:           'The screen is added and paired.',
        confirmed_in:        'The screen is added and paired in {tenant}.',
        tenant:              'Space',
        other_tenant_hint:   'Set the screen\'s group and base content in {tenant}.',
        mode_new:            'New screen',
        mode_existing:       'Existing screen',
        existing_screen:     'Screen',
        existing_pick:       'Pick a screen',
        existing_unpaired:   'Not paired',
        existing_paired:     'Paired (the display will be replaced)',
        default_name:        'Screen',
        link:                'Pairing link',
        short_help:          'On the screen, open {address} (without https://) — it will appear here by itself if you are on the same network.',
        link_or:             'Or the link with this space:',
        link_manual:         'You can also add a screen by hand and enter the code it shows.',
        copy:                'Copy',
        copied:              'Copied',
        copy_failed:         'Copying failed — select the link and copy it by hand.',
    },
    media: {
        title:              'Media',
        subtitle:           'The images and videos your designs use.',
        upload:             'Upload',
        drop:               'Drop the files to upload them',
        drop_help:          'Drag images and videos here or click "Upload". Images JPEG, PNG, WebP, SVG up to 20 MB · videos MP4, WebM up to 300 MB.',
        filter_all:         'All',
        filter_image:       'Images',
        filter_video:       'Videos',
        kind_image:         'Image',
        kind_video:         'Video',
        uploading:          'Uploading {name} …',
        uploaded:           '{n} file uploaded | {n} files uploaded',
        upload_failed_named: '"{name}" could not be uploaded.',
        too_large:          'The file is too large (images up to 20 MB, videos up to 300 MB).',
        too_large_named:    '"{name}" is too large — a {kind} may be at most {limit}.',
        unsupported:        'This kind of file cannot be used.',
        unsupported_named:  '"{name}": JPEG, PNG, WebP and SVG images and MP4 and WebM videos are supported.',
        rename:             'Rename',
        rename_title:       'Rename file',
        name:               'Name',
        delete:             'Delete file',
        confirm_delete:     'Delete "{name}"?',
        deleted:            'The file has been deleted.',
        empty:              'No files yet.',
        empty_filtered:     'No files of this kind.',
        uploaded_by:        '{name}, {date}',
        dimensions:         '{width} × {height}',
    },
    templates: {
        title:               'Presentations',
        subtitle:            'Designs, playlists and templates in your brand colours.',
        tab_designs:         'Designs',
        tab_playlists:       'Playlists',
        tab_templates:       'Templates',
        new_design:          'New design',
        yours:               'Your designs',
        yours_subtitle:      'The designs your screens and schedules show.',
        yours_empty:         'No designs yet. Start from a template in the Templates tab.',
        start:               'Start from a template',
        start_subtitle:      'Templates come in your brand colours and font; everything can be edited afterwards.',
        blank:               'Blank design',
        blank_help:          'An empty canvas in your brand\'s surface colour.',
        edit:                'Edit',
        duplicate:           'Duplicate',
        duplicate_title:     'Duplicate design',
        copy_of:             '{name} (copy)',
        delete:              'Delete design',
        confirm_delete:      'Delete the design "{name}"?',
        deleted:             'The design has been deleted.',
        name:                'Design name',
        name_placeholder:    'Reception — welcome',
        new_title:           'New design',
        new_from:            'From the template "{name}"',
        create:              'Create and edit',
        created:             'The design has been created.',
        published:           'Published · v{version}',
        unpublished_changes: 'Unpublished changes',
        never_published:     'Not published',
        updated:             'Changed {ago}',
        in_use:              'On {screens} screens · in {schedules} rules',
    },
    playlist: {
        title:              'Playlists',
        subtitle:           'Several pages that take turns on a screen — each for its own time, with the transition you choose.',
        empty:              'No playlists yet. A playlist lets a screen rotate several designs.',
        new:                'New playlist',
        edit:               'Edit',
        duplicate:          'Duplicate',
        duplicate_title:    'Duplicate playlist',
        copy_of:            '{name} (copy)',
        delete:             'Delete playlist',
        confirm_delete:     'Delete the playlist "{name}"?',
        deleted:            'The playlist has been deleted.',
        name:               'Playlist name',
        name_placeholder:   'Skyscraper — history',
        default_name:       'New playlist',
        pages:              '{n} page | {n} pages',
        seconds:            '{n} s',
        minutes:            '{m} min',
        minutes_seconds:    '{m} min {s} s',
        hours:              '{h} h',
        hours_minutes:      '{h} h {m} min',
        skipped:            '{n} unpublished page is skipped | {n} unpublished pages are skipped',
        nothing_playable:   'No page is published — screens will not show the playlist.',
        editor_title:       'Playlist',
        pages_title:        'Pages',
        pages_subtitle:     'In the order they play. The same design may appear more than once.',
        pages_empty:        'The playlist has no pages yet. Add the designs that should take turns.',
        add_page:           'Add page',
        page_number:        'Page {n}',
        unpublished_skipped: 'Unpublished — skipped',
        design_gone:        '(deleted design)',
        duration:           'Duration',
        duration_unit:      's',
        duration_rule:      'The duration is 3 to 3,600 seconds.',
        move_up:            'Move up',
        move_down:          'Move down',
        remove_page:        'Remove page',
        open_design:        'Edit design',
        total:              'Total',
        settings:           'Settings',
        transition:         'Transition',
        in_use:             'In use',
        used_screens:       'On {n} screen | On {n} screens',
        used_rules:         'in {n} rule | in {n} rules',
        not_used:           'Not on any screen or in any rule yet.',
        preview:            'Preview',
        preview_unavailable: 'The preview is not available yet.',
        preview_nothing:    'Publish at least one page to preview the playlist.',
        preview_close:      'Close preview',
        saved:              'The playlist has been saved.',
        created:            'The playlist has been created.',
        unsaved:            'Unsaved changes',
        all_saved:          'Saved',
        leave_title:        'Leave the editor?',
        leave_message:      'The playlist has unsaved changes. They are lost if you leave.',
        leave_confirm:      'Discard changes',
        items_required:     'Add at least one page.',
        items_max:          'A playlist can have at most 50 pages.',
        load_failed:        'The playlist could not be loaded.',
        picker_title:       'Add pages',
        picker_subtitle:    'Pick designs — they are added at the end, {n} s each.',
        picker_add:         'Add selected ({n})',
        picker_empty:       'No designs yet. Create one in Presentations first.',
        picker_room:        'You can add {n} more.',
        picker_none_found:  'No design matches the search.',
        add_to:             'Add to playlist',
        add_to_subtitle:    '"{name}" is added at the end of the playlist.',
        add_to_target:      'Playlist',
        add_to_new:         'New playlist with this page',
        add_to_full:        'The playlist is full (50 pages).',
        added:              'The page has been added to "{name}".',
        group_designs:      'Designs',
        group_playlists:    'Playlists',
        playlist_named:     'Playlist: {name}',
        playing_in:         '{page} ({playlist})',
    },
    transition: {
        fade:       'Crossfade',
        cut:        'Cut',
        slide:      'Slide',
        fade_desc:  'The page fades softly into the next one.',
        cut_desc:   'The next page appears at once.',
        slide_desc: 'The next page slides in from the right.',
    },
    style: {
        calm:          'Calm',
        contrast:      'Contrast',
        minimal:       'Minimal',
        calm_desc:     'Natural, elegant, timeless.',
        contrast_desc: 'Bold, modern, effective.',
        minimal_desc:  'Clean, modern, focused.',
    },
    schedule: {
        title:               'Schedules',
        subtitle:            'Publishing rules decide where and when each piece of content is shown.',
        rules:               'Publishing rules',
        rules_subtitle:      'Ordered by priority — when rules overlap, the lower number wins.',
        add:                 'New rule',
        edit:                'Edit rule',
        delete:              'Delete rule',
        form_subtitle:       'Decide where and when your content is shown.',
        name:                'Rule name',
        name_placeholder:    'Breakfast',
        targets:             'Screens',
        targets_placeholder: 'Select screens or groups',
        targets_groups:      'Groups',
        targets_screens:     'Screens',
        targets_required:    'Select at least one screen or group.',
        days:                'Days',
        days_required:       'Select at least one day.',
        time:                'Display time',
        from:                'From',
        to:                  'To',
        same_time:           'The start and the end must differ.',
        midnight:            'Past midnight: the display ends the next day at {to}.',
        midnight_short:      'past midnight',
        dates:               'Period',
        dates_help:          'Optional: the rule applies only between these days (inclusive).',
        dates_order:         'The end of the period cannot be before its start.',
        date_from:           'From day',
        date_to:             'To day',
        date_open:           'no limit',
        content:             'Content',
        design_unpublished:  'This design is not published yet — screens will not show it until you publish it.',
        after_end:           'After the end',
        priority:            'Priority',
        priority_help:       '0 to 1000; the lower number wins when rules overlap.',
        active:              'Active',
        confirm_delete:      'Delete the rule "{name}"?',
        deleted:             'The rule has been deleted.',
        empty:               'No rules yet.',
        empty_help:          'Without rules, screens show their base content.',
        no_designs:          'Create a design in Presentations first.',
        activated:           'The rule "{name}" is switched on.',
        deactivated:         'The rule "{name}" is switched off.',
        unknown:             '(deleted)',
        every_day:           'Every day',
        trigger:             'When',
        price_list:          'Price list',
        price_list_placeholder: 'Select a price list',
        price_list_required: 'Select a price list.',
        price_list_note:     'The rule runs while the price list is active in the POS.',
        price_list_no_menu:  'The POS is not connected yet — connect it in Menu first.',
        price_list_none:     'The POS has no price list besides the default one.',
        by_price_list:       'By price list: {name} ({schedule})',
        by_price_list_gone:  'By a price list the POS no longer has',
    },
    weekday: {
        d1: 'Mon',
        d2: 'Tue',
        d3: 'Wed',
        d4: 'Thu',
        d5: 'Fri',
        d6: 'Sat',
        d7: 'Sun',
    },
    after_end: {
        default: 'Base content',
        off:     'Screen off',
    },
    branding: {
        title:              'Brand and display',
        subtitle:           'Adapt the look of your screens to your brand and your space.',
        save:               'Save changes',
        saved:              'The changes have been saved.',
        discard:            'Discard',
        unsaved:            'Unsaved changes',
        identity:           'Brand identity',
        identity_subtitle:  'Decide how your brand looks on the screens.',
        logo:               'Logo',
        logo_upload:        'Upload logo',
        logo_help:          'PNG, SVG or JPG (max. 5 MB)',
        logo_replace:       'Replace',
        logo_remove:        'Remove',
        logo_too_large:     'The logo is larger than 5 MB.',
        logo_type:          'The logo must be a PNG, SVG or JPG.',
        logo_failed:        'The logo could not be uploaded.',
        colors:             'Colours',
        color_0:            'Primary',
        color_1:            'Surface',
        color_2:            'Accent',
        color_pick:         'Pick a colour: {name}',
        color_invalid:      'A colour must be written as #RRGGBB.',
        font:               'Font',
        domain:             'Web address',
        domain_placeholder: 'screens.your-domain.com',
        domain_invalid:     'Enter a host name, e.g. screens.hotel.com.',
        provider_brand:     'Show the provider brand',
        provider_brand_off: 'The provider logo will not be shown on the screens.',
        provider_brand_on:  'The provider brand will be shown discreetly on the screens.',
        location:           'Location',
        location_subtitle:  'For the weather on screens without a location of their own.',
        preview:            'Live preview',
        preview_subtitle:   'This is how your content will look on the screens.',
        preview_screen:     'Screen to preview',
        pick_template:      'Choose a template',
        pick_template_help: 'Adapt the look quickly with a ready-made template.',
        layout:             'Layout',
        layout_help:        'Choose the display orientation for new content.',
        read_only:          'The brand is managed by the customer\'s administrator — you can only look at it.',
    },
    look: {
        title:                    'Looks',
        subtitle:                 'Other brands in the same place — e.g. a meeting room several companies use. Switch a screen to one under Screens.',
        default:                  'Default look',
        default_help:             'The default look is the brand identity above.',
        add:                      'New look',
        edit:                     'Edit look',
        delete:                   'Delete look',
        confirm_delete:           'Delete the look {name}? Screens and meetings that use it go back to the default look.',
        deleted:                  'The look has been deleted.',
        added:                    'The look has been added.',
        saved:                    'The look has been saved.',
        empty_help:               'Add a look for each company that uses the place: logo, colours and font.',
        limit:                    'At most {n} looks.',
        name:                     'Name',
        name_placeholder:         'IT TEAM',
        logo:                     'Logo',
        logo_pick:                'From Media',
        logo_upload:              'Upload',
        logo_none:                'No logo',
        share_domain:             'Screen sharing address',
        share_domain_placeholder: 'share.your-domain.com',
        share_domain_help:        'A screen in this look shows this address for sharing (…/deli). The provider sets up the route to the server (edge) and its certificate by hand.',
        preview:                  'Preview',
        select:                   'Screen look',
        changed:                  'The screen {name} is now in the look {look}.',
    },
    service_pin: {
        button:        'Service code',
        title:         'Screen service code',
        is_set:        'A service code is set. For security it cannot be shown — you can replace or remove it.',
        not_set:       'No service code is set: the service menu does not open on paired screens.',
        badge_set:     'set',
        badge_not_set: 'not set',
        help_open:     'The code opens the service menu of the Imprion DS app for Apple TV: hold Play/Pause on the remote for 3 seconds.',
        help_without:  'Without a code the service menu does not open on paired screens.',
        help_tvos:     'The code does not unlock the tvOS settings — only the app\'s service menu.',
        pin:           'Code',
        new_pin:       'New code',
        pin_help:      '4 to 8 digits. It applies to every screen of this space.',
        show:          'Show the code',
        hide:          'Hide the code',
        save:          'Set code',
        change:        'Replace code',
        remove:        'Remove code',
        confirm_remove: 'Remove the service code? The service menu will no longer open on paired screens until you set a new one.',
        saved:         'The service code has been set.',
        removed:       'The service code has been removed — the service menu no longer opens on paired screens.',
        invalid:       'The code must have 4 to 8 digits.',
        failed:        'The service code could not be saved.',
    },
    meeting: {
        button:            'Meeting',
        title:             'Meeting · {name}',
        description:       'Until the meeting ends, the screen shows the chosen content and welcomes the guest.',
        guest:             'Guest',
        guest_placeholder: 'Naturalica',
        guest_help:        "The name replaces {'{guest}'} in the content.",
        guest_logo:        'Guest logo',
        logo_pick:         'From Media',
        logo_upload:       'Upload',
        logo_remove:       'Remove the guest logo',
        content:           'Content',
        content_none:      'Nothing is published — publish a design in the editor.',
        content_required:  'Choose what the screen shows.',
        look:              'Look during the meeting',
        look_screen:       'As the screen ({name})',
        duration:          'Duration',
        durations: {
            '1h':         '1 h',
            '2h':         '2 h',
            '4h':         '4 h',
            end_of_day:   'Until end of day',
            custom:       'Until …',
            keep:         'As now (until {time})',
        },
        custom_end:        'Ends at',
        custom_invalid:    'Enter a time, e.g. 16:30.',
        ends:              'Ends: {when}',
        preview:           'Preview',
        start:             'Start meeting',
        update:            'Update meeting',
        started:           'The meeting has started — the screen shows it within half a minute.',
        updated:           'The meeting has been updated.',
        start_failed:      'The meeting could not be started.',
        end:               'End',
        ended:             'The meeting has ended.',
        end_failed:        'The meeting could not be ended.',
        until:             'until {time}',
        running:           'Meeting · {until}',
        running_named:     'Meeting: {guest} · {until}',
        // A meeting's own canvas ("Customize for this meeting", docs/API.md).
        quick_title:               'Texts and pictures',
        quick_help:                'Changes apply to this meeting only — the design stays as it is.',
        quick_empty:               'This design has no texts or pictures. Change anything else with "Customize for this meeting".',
        customized:                'Customized',
        hide:                      'Hide',
        show:                      'Show',
        hidden:                    'Hidden',
        guest_token_hint:          "The screen shows the guest's name instead of {token}.",
        role_brand_note:           "The brand logo — the look during the meeting sets the picture.",
        role_guest_note:           'The guest\'s logo — chosen above under "Guest logo".',
        image_failed:              'The picture could not be uploaded.',
        reset:                     'Reset',
        reset_title:               'Reset the customization?',
        reset_message:             'The changes for this meeting are discarded — the screen shows the design as published.',
        content_change_title:      'Change the content?',
        content_change:            'The changes for this meeting do not apply to other content and will be discarded.',
        content_change_confirm:    'Change',
        customize:                 'Customize for this meeting',
        customize_title:           'Customize for this meeting',
        customize_subtitle:        '{name} — changes apply to this meeting only',
        apply:                     'Apply',
        customize_discard_title:   'Discard the changes?',
        customize_discard:         'The changes made in the editor are discarded; the meeting stays as it was.',
        customize_discard_confirm: 'Discard',
        save_design:               'Save as design',
        save_design_title:         'Save as design',
        save_design_description:   'The customization becomes a new published design under Presentations, and the meeting shows it.',
        save_design_name:          '{design} — {guest}',
        save_design_name_copy:     '{design} — meeting',
        save_design_failed:        'The design could not be saved.',
        design_saved:              'Saved as the design "{name}" — the meeting shows it.',
    },
    menu: {
        title:                   'Menu',
        subtitle:                'Items from the Imprion POS, ready to be shown on screens.',
        sync:                    'Refresh from the POS',
        synced:                  'Refreshed from the POS — added: {added}, updated: {updated}, removed: {removed}.',
        sync_failed:             'Refreshing from the POS failed.',
        connection:              'POS connection',
        last_sync:               'Last sync: {ago}',
        never_synced:            'The menu has not been loaded from the POS yet.',
        last_error:              'The last sync failed: {error}',
        products_count:          '{n} item | {n} items',
        auto_every:              'automatically every {n} min',
        auto_off:                'automatic sync is off',
        not_connected:           'The POS is not connected yet',
        not_connected_help:      'Connect the Imprion POS: items, categories and price lists load by themselves, and the Menu and Happy hour elements show them on screens.',
        not_connected_editor:    'A tenant admin has to connect the POS.',
        connect:                 'Connect the POS',
        not_synced:              'The menu is not loaded yet',
        not_synced_help:         'The POS is connected. Click "Refresh from the POS" to load the items.',
        tab_products:            'Items',
        tab_categories:          'Categories',
        tab_price_lists:         'Price lists (Happy hour)',
        search:                  'Search by name or code …',
        filter_all:              'All',
        filter_hidden:           'Hidden',
        filter_happy:            'With a happy hour price',
        filter_removed:          'Removed',
        empty_products:          'The POS has no items.',
        empty_filtered:          'No item matches.',
        no_category:             'No category',
        category_hidden:         'Hidden on screens',
        hidden:                  'Hide on screens',
        show_on_screens:         'Show on screens',
        hidden_badge:            'Hidden',
        removed:                 'No longer in the POS',
        disabled:                'Disabled in the POS',
        product_hidden:          '"{name}" is hidden on screens.',
        product_shown:           '"{name}" is shown on screens again.',
        edit_product:            'Edit item',
        edit_product_subtitle:   'How it shows on screens — names and prices in the POS stay as they are.',
        display_name:            'Display name',
        display_name_help:       'Empty = the name from the POS.',
        description:             'Description',
        image:                   'Image',
        image_pick:              'Pick from Content',
        image_replace:           'Replace',
        image_remove:            'Remove',
        image_none:              'No image',
        framing:                 'Photo framing',
        framing_help:            'Drag the photo to choose which part stays in view in a menu\'s box; zoom brings it closer.',
        framing_drag:            'Move the photo in its box (arrow keys)',
        framing_value:           'Horizontal {x} %, vertical {y} %',
        framing_shapes:          'In each shape',
        framing_zoom:            'Zoom',
        framing_reset:           'Reset',
        tags:                    'Tags',
        position:                'Order',
        position_help:           'A lower number comes first in its category; equal ones follow by name.',
        pos_facts:               'From the POS',
        pos_facts_help:          'The POS owns these — they refresh with every sync.',
        pos_name:                'Name in the POS',
        code:                    'Code',
        price:                   'Price',
        vat:                     'VAT',
        category:                'Category',
        list_prices:             'Prices by price list',
        list_prices_none:        'Not on any additional price list.',
        product_saved:           'The item is saved.',
        categories_empty:        'The POS has no categories.',
        category_pos_name:       'In the POS: {name}',
        category_display_name:   'Category display name',
        category_saved:          'The category is saved.',
        category_hidden_toast:   'The category "{name}" is hidden on screens.',
        category_shown_toast:    'The category "{name}" is shown on screens again.',
        order_saved:             'The order is saved.',
        move_up:                 'Move up',
        move_down:               'Move down',
        price_lists_help:        'Set happy hour times in the POS (back office → Price lists). Screens read them at every sync.',
        price_lists_empty:       'The POS has no price lists.',
        default_list:            'Default',
        default_list_help:       'The base prices — they apply while no other price list is active.',
        list_active:             'Switched on in the POS',
        active_now:              'Active now',
        priority:                'Priority {n}',
        priority_help:           'When several price lists are active, the higher priority wins.',
        mapping_note:            'Reading the hours from the POS is still being verified. If the schedule here does not match the POS, send the data below to support.',
        raw_conditions:          'Data from the POS',
        every_day:               'every day',
        all_day:                 'all day',
        date_open:               'no limit',
        weekday: {
            d1: 'Mon',
            d2: 'Tue',
            d3: 'Wed',
            d4: 'Thu',
            d5: 'Fri',
            d6: 'Sat',
            d7: 'Sun',
        },
        connection_subtitle:     'Screens only read from the Imprion POS — they never write to it.',
        environment:             'Environment',
        token:                   'API token',
        token_placeholder:       'Paste the API token',
        token_kept:              'A token is stored — leave empty to keep it.',
        token_required:          'Enter the API token.',
        business_unit:           'Business unit',
        business_unit_placeholder: 'Load the business units first',
        business_unit_pick:      'Select a business unit',
        business_unit_required:  'Select a business unit.',
        business_units_load:     'Load business units',
        business_units_loaded:   'The business units are loaded.',
        business_units_none:     'The POS returned no business unit.',
        business_units_failed:   'The business units could not be loaded.',
        auto_sync:               'Automatic sync',
        auto_sync_help:          'The menu refreshes from the POS by itself at the chosen interval.',
        interval:                'Interval',
        minutes:                 '{n} min',
        connection_saved:        'The POS connection is saved.',
    },
    menu_tag: {
        new:         'New',
        recommended: 'Recommended',
        vegan:       'Vegan',
        vegetarian:  'Vegetarian',
        spicy:       'Spicy',
        gluten_free: 'Gluten-free',
        local:       'Local',
    },
    pos_environment: {
        production: 'Production',
        demo:       'Demo',
    },
    rule_trigger: {
        time:       'At set times',
        price_list: 'By a POS price list',
    },
    design: designEn,
} satisfies MessageSchema

export default en
